import React, { useEffect, useRef, useState } from 'react';
import '@aerialist7/react-dates/initialize';
import PropTypes from 'prop-types';
import moment from 'moment';
import { DayPickerSingleDateController, isSameDay } from '@aerialist7/react-dates';

import { SVGIcon, colors } from '@custom-components';

import * as Styled from './SingleDatePicker.styles';

const LL_DATE_FORMAT = 'YYYY-MM-DD';

function SingleDatePicker(props) {
  const {
    children,
    inputBorderType,
    mobileDaySize,
    labelText,
    blockedDays,
    onDateChangeHook,
    initialDate,
    disclaimerCopy,
    open,
    fullScreen,
    noInputs,
    pickerPosition,
    noArrow,
    dateFormat,
    name,
    ...rest
  } = props;

  const resolveIncomingDate = (dateString) => {
    if (typeof dateString !== 'string') {
      return null;
    }

    if (dateString instanceof moment) {
      return dateString;
    }

    const momentDate = moment(dateString, LL_DATE_FORMAT);
    return momentDate.isValid() ? momentDate : null;
  };

  const [focused, setFocused] = useState(true);
  const [date, setDate] = useState(resolveIncomingDate(initialDate));
  const [visible, setVisible] = useState(open);

  const node = useRef(undefined);

  const resolveDateStrings = (format = LL_DATE_FORMAT, newDate = date) =>
    newDate ? newDate.format(format) : undefined;

  const closeDayPicker = () => {
    setVisible(false);

    if (!date) {
      setDate(undefined);
      setFocused(false);
    }
  };

  const handleClickListener = ({ target }) => {
    if (!node.current?.contains) return false;
    if (node.current?.contains(target)) return false;

    closeDayPicker();
    document.removeEventListener('mousedown', handleClickListener, true);

    return true;
  };

  useEffect(() => {
    if (!!date && visible) {
      closeDayPicker();
    }
  }, [date]);

  const onDateChange = (newDate) => {
    setDate(newDate);
    document.removeEventListener('mousedown', handleClickListener, true);
    const resolvedDate = resolveDateStrings(LL_DATE_FORMAT, newDate);
    onDateChangeHook({ target: { value: resolvedDate, name } });
  };

  const onFocusChange = ({ focused: newFocused }) => setFocused(newFocused);

  const createWrapperNode = (newNode) => {
    node.current = newNode;
  };

  const resetDate = () => {
    setDate('');
    setFocused(false);
    setVisible(false);
    document.removeEventListener('mousedown', handleClickListener, true);
    onDateChangeHook({ target: { value: undefined } });
  };

  const resolveBlockedDays = (currDay) => {
    const verifyKeys = (i) =>
      Object.prototype.hasOwnProperty.call(i, 'start') && Object.prototype.hasOwnProperty.call(i, 'end');

    return blockedDays.some((day) => {
      if (typeof day === 'object' && verifyKeys(day)) {
        const start = resolveIncomingDate(day.start);
        const end = resolveIncomingDate(day.end);
        return currDay.isBetween(start, end);
      }
      return isSameDay(currDay, resolveIncomingDate(day));
    });
  };

  useEffect(() => {
    if (visible) {
      document.addEventListener('mousedown', handleClickListener, true);
    }
  }, [visible]);

  const openDayPicker = () => {
    setVisible(true);
  };

  /**
   * Render helper methods to clean up our render()
   */
  function renderCalendarInfo() {
    if (!disclaimerCopy) return null;
    return (
      <Styled.CopyMini color="tertiary" align="center">
        {disclaimerCopy}
      </Styled.CopyMini>
    );
  }

  const dateString = resolveDateStrings(dateFormat);
  const inputValue = dateString || undefined;

  const sharedProps = {
    ...rest,
    renderCalendarInfo,
    isDayBlocked: blockedDays ? resolveBlockedDays : undefined,
    numberOfMonths: 1
  };

  const requiredProps = {
    date,
    focused,
    onDateChange,
    onFocusChange
  };

  return (
    <Styled.Wrapper fullScreen={fullScreen} ref={createWrapperNode}>
      {children({
        date,
        resolver: resolveDateStrings,
        openPicker: openDayPicker,
        closePicker: closeDayPicker,
        visible,
        resetDate,
        inputValue
      })}
      <Styled.DayPickerWrapper
        visible={fullScreen ? true : visible}
        fullScreen={fullScreen}
        pickerPosition={pickerPosition}
        noArrow={noArrow}
        data-testid="singleDatePickerWrapper"
      >
        <DayPickerSingleDateController
          key="lg-breakpoint-datepicker"
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...sharedProps}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...requiredProps}
          initialVisibleMonth={() => date || moment()}
        />
      </Styled.DayPickerWrapper>
    </Styled.Wrapper>
  );
}

SingleDatePicker.propTypes = {
  /* Array of blocked days: can be either LL Date strings or a range object with a start/end property with values that are also LL Date. */
  blockedDays: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.shape({ start: PropTypes.string, end: PropTypes.string })])
  ),

  /* Disclaimer Copy for the bottom of the SingleDatePicker */
  disclaimerCopy: PropTypes.string,

  /* LL Date (YYYY/MM/DD) format to seed intial date. */
  initialDate: PropTypes.string,

  /* Fullscreen Mode - this is mainly for the Map screen */
  fullScreen: PropTypes.bool,

  /* Label Text for the input field */
  labelText: PropTypes.string,

  /* Boolean to toggle the inputs off without doing an empty render prop function */
  noInputs: PropTypes.bool,

  /* Hook fired when onDateChange is fired by the DayPickerRangeController  */
  onDateChangeHook: PropTypes.func,

  /* Flag to toggle whether the SingleDatePicker is visible on mount or not. Useful for the render prop implementation */
  open: PropTypes.bool,

  /* One of 'left', 'center' or 'right' to position the picker. Defaults to left. */
  pickerPosition: PropTypes.oneOf(['left', 'center', 'right', 'topRight']),

  /* Flag to toggle off the arrow on the top of the SingleDatePicker pointing to the input field */
  noArrow: PropTypes.bool,

  /* Below are default props for the react-dates implementation being defined */
  numberOfMonths: PropTypes.number,
  isDayBlocked: PropTypes.func,
  isOutsideRange: PropTypes.func,
  isDayHighlighted: PropTypes.func,
  keepOpenOnDateSelect: PropTypes.bool,
  hideKeyboardShortcutsPanel: PropTypes.bool,
  navNext: PropTypes.node,
  navPrev: PropTypes.node,
  monthFormat: PropTypes.string,

  children: PropTypes.node,
  inputBorderType: PropTypes.string,
  mobileDaySize: PropTypes.number,
  dateFormat: PropTypes.string,
  name: PropTypes.string
};

SingleDatePicker.defaultProps = {
  /**
   * Not to be confused with the onDateChange prop for DayPicker, this prop is fired as a hook when the internal DateChange method is executed
   * Currently returns dates in different either moment or JSDate formats depending on props.
   */
  onDateChangeHook: () => {},

  /** Date */
  initialDate: null,

  // day presentation and interaction related props
  isDayBlocked: () => false,
  isDayHighlighted: () => false,
  isOutsideRange: () => {},
  keepOpenOnDateSelect: false,

  // calendar presentation and interaction related props
  numberOfMonths: 2,
  hideKeyboardShortcutsPanel: true,

  // navigation related props
  navPrev: <SVGIcon selectedIcon="arrow" position="static" rotate={90} iconColor={colors.colorBlue} />,
  navNext: <SVGIcon selectedIcon="arrow" position="static" rotate={-90} iconColor={colors.colorBlue} />,

  // internationalization
  monthFormat: 'MMMM YYYY',
  disclaimerCopy: null,
  labelText: 'Date',

  // new props
  open: false,
  noInputs: false,
  fullScreen: false,
  blockedDays: [],
  pickerPosition: 'left',
  noArrow: false,

  children: () => {},
  inputBorderType: '',
  mobileDaySize: 30,
  dateFormat: 'MMM DD, YYYY',
  name: ''
};

export default SingleDatePicker;
